import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SecurityScope } from '../../auth/auth.model';
import { InsightService } from '../../insight/insight.service';
import { DataFilterOptions } from '../../performance-data/data-filter-options.pd.model';
import { PerformanceDataViewType } from '../../performance-data/metric.pd.model';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { PersonOfInterestModel } from '../../user/people-of-interest.user.model';
import { PeopleOfInterestUserService } from '../../user/people-of-interest.user.service';
import { PeriodType } from '../../widget/date-range.widget.model';
import { PerformanceMetricData } from '../models/performance-metric-data.model';
import { ActionType } from '../my-performance-card.action';
import { MyPerformanceCardService } from '../my-performance-card.service';
import { PerformanceCardState } from '../my-performance-card.state';

import {
    GetMetricPerformanceDataAction,
    getMetricPerformanceDataResult,
    GetMetricPerformanceDataResultAction,
} from './metric-performance-data.action';

function buildDataFilterOptions(
    roleId: number,
    isRoleAgent: boolean,
    superGroupIdList: number[],
    latestEndDate: Date,
    metricIds: number[],
    userId: number,
    viewType: PerformanceDataViewType,
    periodType: PeriodType,
    managerId?: number,
): DataFilterOptions {
    return {
        roleId,
        superGroupIdList,
        date: latestEndDate,
        metricIdList: metricIds,
        viewType,
        displayNameCriteriaIdType: null,
        outputCriteriaIdType: null,
        periodType,
        userIdList: isRoleAgent ? [userId] : null,

        startDate: null,
        managerIdList: !isRoleAgent ? [managerId] : null,

        queueIdList: [],
        peerGroupIdList: [],
        siteIdList: [],
        areaIdList: [],
        regionIdList: [],
        lobIdList: [],
    };
}

function fetchMetricData(
    service: MyPerformanceCardService,
    poiService: PeopleOfInterestUserService,
    baseOptions: DataFilterOptions,
    metricData: PerformanceMetricData[],
): Observable<[PerformanceMetricData[], PersonOfInterestModel[], number[]]> {
    const uniqueUserIds = Array.from(
        new Set(metricData.map((item) => item.userId)),
    );

    return forkJoin({
        hierarchyInfos: service.listHierarchyUserInfo(uniqueUserIds),
        poiData: of(null).pipe(
            poiService.list(() => SecurityScope.COACHING),
            map((data) => (Array.isArray(data) ? data : [])),
        ),
    }).pipe(
        switchMap(({ hierarchyInfos, poiData }) => {
            const uniqueSuperGroupIds = Array.from(
                new Set(hierarchyInfos.map((info) => info.superGroupId)),
            );

            const userIdsHaveOtherManagers =
                hierarchyInfos
                    ?.filter(
                        (e) => e.managerId !== baseOptions?.managerIdList?.[0],
                    )
                    .map(({ userId }) => userId) ?? [];

            const supergroupOptions: DataFilterOptions = {
                ...baseOptions,
                superGroupIdList: uniqueSuperGroupIds,
                viewType: PerformanceDataViewType.SUPER_GROUP,
                userIdList: uniqueUserIds,
            };

            return service.listData(supergroupOptions).pipe(
                map((supergroupData: PerformanceMetricData[]) => {
                    const updatedMetrics = metricData.map((item) => {
                        const matchingSupergroup = supergroupData.find(
                            (sgItem) => sgItem.metricID === item.metricID,
                        );
                        return {
                            ...item,
                            supergroupActual:
                                matchingSupergroup?.actual ?? null,
                        };
                    });

                    const availableUsers = poiData.filter(
                        (user) => user.isAvailable,
                    );

                    return [
                        updatedMetrics,
                        availableUsers,
                        userIdsHaveOtherManagers,
                    ] as [
                        PerformanceMetricData[],
                        PersonOfInterestModel[],
                        number[],
                    ];
                }),
            );
        }),
    );
}

const getMetricPerformanceDataEpic = (
    action: ActionsObservable<
        PayloadAction<ActionType, GetMetricPerformanceDataAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<
    PayloadAction<ActionType, GetMetricPerformanceDataResultAction>
> =>
    action.pipe(
        ofType(ActionType.MY_PERFORMANCE_CARD__GET_METRIC_PERFORMANCE_DATA),
        switchMap((act) => {
            const service = deps.injector.get<MyPerformanceCardService>(
                MyPerformanceCardService,
            );
            const _PeopleOfInterestUserService =
                deps.injector.get<PeopleOfInterestUserService>(
                    PeopleOfInterestUserService,
                );

            const insightService =
                deps.injector.get<InsightService>(InsightService);

            const rootState = pluck<PerformanceCardState>(
                state.value,
                act.storePath,
            );
            const userId = act.payload.userId;
            const card =
                rootState.userData[act.payload.userId]?.performanceCard;
            const metricIds =
                card?.total.colourGroups
                    ?.flatMap(
                        (group) =>
                            group.metrics?.map((metric) => metric.metricID),
                    )
                    ?.filter((id): id is number => id !== undefined) ?? [];
            const dateValue = card?.endDate ?? 0;
            const date = new Date(dateValue);

            return forkJoin([
                service.retrieveCardConfig(),
                insightService.listRoleHierarchy(),
                insightService.listHierarchyUserInfo([userId]),
            ]).pipe(
                switchMap(([cardConfig, roleHierarchy, userHierarchyInfo]) => {
                    const roleId = userHierarchyInfo?.[0]?.roleId;

                    const nextRoleId =
                        roleHierarchy?.find((e) => e.parentRoleId === roleId)
                            ?.roleId ?? roleId;
                    const isRoleAgent =
                        roleId === (cardConfig[0]?.agentRoleId ?? 0);
                    const viewType =
                        roleId === (cardConfig[0]?.agentRoleId ?? 0)
                            ? PerformanceDataViewType.USER_QUEUE
                            : PerformanceDataViewType.USER_SUPER_GROUP;

                    const baseOptions = buildDataFilterOptions(
                        nextRoleId,
                        isRoleAgent,
                        [],
                        date,
                        metricIds,
                        userId,
                        viewType,
                        PeriodType.MONTH,
                        userId,
                    );

                    return service.listData(baseOptions).pipe(
                        switchMap(
                            (metricPerformanceData: PerformanceMetricData[]) =>
                                fetchMetricData(
                                    service,
                                    _PeopleOfInterestUserService,
                                    baseOptions,
                                    metricPerformanceData,
                                ).pipe(
                                    map(
                                        ([
                                            mergedMetricData,
                                            cmpsUsers,
                                            userIdsHaveOtherManagers,
                                        ]) =>
                                            getMetricPerformanceDataResult(
                                                {
                                                    metricPerformanceData:
                                                        mergedMetricData,
                                                    poiUserData: cmpsUsers,
                                                    userIdsHaveOtherManagers,
                                                    userId: act.payload.userId,
                                                },
                                                act.storePath,
                                            ),
                                    ),
                                ),
                        ),
                    );
                }),
                catchError(() =>
                    of(
                        getMetricPerformanceDataResult(
                            {
                                metricPerformanceData: [],
                                poiUserData: [],
                                userId: act.payload.userId,
                                userIdsHaveOtherManagers: [],
                            },
                            act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

export const metricPerformanceDataEpics = [getMetricPerformanceDataEpic];
